import { z } from "zod";
import { runStateSchema } from "./states";

/**
 * Work item statuses, as the WorkItems board tabs them. A run snapshots the item it starts
 * from, so a later edit never changes a run already going.
 */
export const workItemStatuses = ["intake", "ready", "in-progress", "in-review", "done"] as const;
export const workItemStatusSchema = z.enum(workItemStatuses);
export type WorkItemStatus = z.infer<typeof workItemStatusSchema>;

/** Work item priorities, as the board's Priority column shows them. */
export const workItemPriorities = ["low", "medium", "high"] as const;
export const workItemPrioritySchema = z.enum(workItemPriorities);
export type WorkItemPriority = z.infer<typeof workItemPrioritySchema>;

/**
 * One row in `GET /api/v1/projects/:id/work-items`: key, title, source, status, priority, the
 * latest run, the run, pull request, and comment counts for the board card, and when it was
 * updated. The latest run is null when the item never started one.
 */
export const workItemSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  key: z.string().min(1),
  title: z.string().min(1),
  source: z.string().min(1),
  status: workItemStatusSchema,
  priority: workItemPrioritySchema,
  description: z.string().nullable(),
  latestRunId: z.uuid().nullable(),
  latestRunState: runStateSchema.nullable(),
  runCount: z.number().int().min(0),
  pullRequestCount: z.number().int().min(0),
  commentCount: z.number().int().min(0),
  updatedAt: z.iso.datetime(),
});
export type WorkItem = z.infer<typeof workItemSchema>;

/** The work item filters: all plus one tab per status, with a key-or-title search. */
export const workItemFilters = ["all", ...workItemStatuses] as const;
export const workItemFilterSchema = z.enum(workItemFilters);
export type WorkItemFilter = z.infer<typeof workItemFilterSchema>;

export const workItemQuerySchema = z.object({
  status: workItemFilterSchema.optional().default("all"),
  search: z.string().trim().max(120).optional().default(""),
  page: z.coerce.number().int().min(1).optional().default(1),
  pageSize: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type WorkItemQuery = z.infer<typeof workItemQuerySchema>;

export const workItemListResponseSchema = z.object({
  items: z.array(workItemSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
});
export type WorkItemListResponse = z.infer<typeof workItemListResponseSchema>;

/** `POST /api/v1/projects/:id/work-items`. Keys read `ABC-123` and are unique per project. */
export const createWorkItemSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(24)
    .regex(/^[A-Z0-9]+-[0-9]+$/, "Keys read ABC-123: capitals, digits, one hyphen."),
  title: z.string().trim().min(1).max(200),
  source: z.string().trim().min(1).max(60),
  status: workItemStatusSchema.optional().default("intake"),
  priority: workItemPrioritySchema.optional().default("medium"),
  description: z.string().trim().max(2000).optional(),
});
export type CreateWorkItem = z.infer<typeof createWorkItemSchema>;

/**
 * `GET /api/v1/projects/:id/work-items/:key`: the item plus its runs, newest first, for the
 * detail drawer.
 */
export const workItemRunSchema = z.object({
  id: z.uuid(),
  state: runStateSchema,
  startedAt: z.iso.datetime(),
});
export type WorkItemRun = z.infer<typeof workItemRunSchema>;

/**
 * One pull request opened by a run of this work item, as the detail drawer lists it. Pull
 * requests are read through the run that opened them, so no second link is stored.
 */
export const workItemPullRequestSchema = z.object({
  id: z.uuid(),
  title: z.string().min(1),
  head: z.string().min(1),
  base: z.string().min(1),
  runId: z.uuid().nullable(),
  checks: z.string().min(1),
  reviewState: z.string().min(1),
  mergeState: z.string().min(1),
  url: z.string().nullable(),
});
export type WorkItemPullRequest = z.infer<typeof workItemPullRequestSchema>;

/**
 * Who wrote a work item comment. `agent` marks a labelled Zot reply to an `@zot` mention;
 * everything else is a person. The author id of an agent reply names the commenter whose
 * mention triggered it, so the row keeps its author reference; the badge reads the origin.
 */
export const workItemCommentOrigins = ["user", "agent"] as const;
export const workItemCommentOriginSchema = z.enum(workItemCommentOrigins);
export type WorkItemCommentOrigin = z.infer<typeof workItemCommentOriginSchema>;

/**
 * One record an agent reply may cite. Mirrors the citation shape Zot answers use, kept local
 * so work items have no runtime dependency on the Zot module.
 */
export const workItemCommentCitationSchema = z.object({
  kind: z.string().min(1),
  ref: z.string().min(1),
  label: z.string().min(1),
});
export type WorkItemCommentCitation = z.infer<typeof workItemCommentCitationSchema>;

/**
 * One comment on a work item, as the thread shows it: author, plain-text body, and a date for
 * people. The body is stored as plain text and rendered as quoted text, never as markup. Agent
 * replies carry their cited records alongside the body; human comments carry none.
 */
export const workItemCommentSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  workItemId: z.uuid(),
  authorId: z.uuid(),
  authorName: z.string().min(1),
  body: z.string().min(1),
  origin: workItemCommentOriginSchema.default("user"),
  citations: z.array(workItemCommentCitationSchema).default([]),
  createdAt: z.iso.datetime(),
});
export type WorkItemComment = z.infer<typeof workItemCommentSchema>;

export const workItemCommentListSchema = z.array(workItemCommentSchema);
export type WorkItemCommentList = z.infer<typeof workItemCommentListSchema>;

/**
 * Bounds for the work item detail read, so one popular task cannot grow its runs, pull
 * requests, or comments without limit. Runs and pull requests read newest first; comments
 * read newest first and display oldest first, with the totals beside them staying exact.
 */
export const WORK_ITEM_DETAIL_RUN_LIMIT = 20;
export const WORK_ITEM_DETAIL_PULL_REQUEST_LIMIT = 20;
export const WORK_ITEM_DETAIL_COMMENT_LIMIT = 100;

/** `POST /api/v1/projects/:id/work-items/:key/comments`. The body is plain text. */
export const createWorkItemCommentSchema = z.object({
  body: z.string().trim().min(1).max(2000),
});
export type CreateWorkItemComment = z.infer<typeof createWorkItemCommentSchema>;

/** `PATCH /api/v1/projects/:id/work-items/:key`. Moves the item between statuses. */
export const updateWorkItemSchema = z.object({
  status: workItemStatusSchema,
});
export type UpdateWorkItem = z.infer<typeof updateWorkItemSchema>;

export const workItemDetailSchema = workItemSchema.extend({
  runs: z.array(workItemRunSchema),
  pullRequests: z.array(workItemPullRequestSchema),
  comments: z.array(workItemCommentSchema),
});
export type WorkItemDetail = z.infer<typeof workItemDetailSchema>;
